import { Space, Button, Popconfirm, Tag } from "antd";
import { useSubmit } from "react-router-dom";
import { useRouteActivity } from "~/hooks/useRouteActivity";
import { useTranslation } from "react-i18next";
import { EnrichedJoinRequest } from "~/types/organization";
import { CustomTable, TableHeaderInfo } from "~/components";
import { CustomTextField } from "~/components/TextField";
import { ColumnsType } from "antd/es/table";
import { dateToLocalTime, INTENTS } from "~/utils";
import * as APITypes from "~/API";

export const JoinRequestsTab = ({
  joinRequests,
}: {
  joinRequests: EnrichedJoinRequest[];
}) => {
  const { t } = useTranslation();
  const submit = useSubmit();
  const { isFormBusy } = useRouteActivity();

  const handleApprove = (requestID: string) => {
    const formData = new FormData();
    formData.append("intent", INTENTS._approve_join_request);
    formData.append("requestID", requestID);
    submit(formData, { method: "post" });
  };

  const handleDeny = (request: APITypes.JoinOrganizationRequest) => {
    const formData = new FormData();
    formData.append("intent", INTENTS._deny_join_request);
    formData.append(
      "body",
      JSON.stringify({
        id: request.id,
        _version: request._version,
      })
    );
    submit(formData, { method: "post" });
  };

  const columns: ColumnsType<EnrichedJoinRequest> = [
    {
      title: t("Username"),
      dataIndex: ["user", "username"],
      key: "username",
      width: 150,
      render: (username: string) => (
        <CustomTextField
          value={username}
          onEmpty="none"
          style={{ fontWeight: 500 }}
        />
      ),
    },
    {
      title: t("First_Name"),
      dataIndex: ["user", "firstName"],
      key: "firstName",
      width: 150,
      render: (firstName: string) => (
        <CustomTextField value={firstName} onEmpty="none" />
      ),
    },
    {
      title: t("Last_Name"),
      dataIndex: ["user", "lastName"],
      key: "lastName",
      width: 150,
      render: (lastName: string) => (
        <CustomTextField value={lastName} onEmpty="none" />
      ),
    },
    {
      title: t("Email"),
      dataIndex: ["user", "email"],
      key: "email",
      width: 200,
      render: (email: string) => (
        <CustomTextField value={email} onEmpty="none" />
      ),
    },
    {
      title: t("Property_Transfer"),
      dataIndex: ["request", "requestPropertyTransfer"],
      key: "requestPropertyTransfer",
      width: 150,
      align: "center",
      render: (requestPropertyTransfer: boolean) => (
        <Tag color={requestPropertyTransfer ? "green" : "default"}>
          {requestPropertyTransfer
            ? t("Will_Transfer_Properties")
            : t("No_Property_Transfer")}
        </Tag>
      ),
    },
    {
      title: t("Request_Date"),
      dataIndex: ["request", "createdAt"],
      key: "createdAt",
      width: 180,
      render: (date: string) =>
        date ? dateToLocalTime(date, "dateTime") : "-",
    },
    {
      title: t("Actions"),
      key: "actions",
      width: 200,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Popconfirm
            title={t("Approve_Request_Confirm")}
            onConfirm={() => handleApprove(record.request.id)}
            okText={t("Confirm")}
            cancelText={t("Cancel")}
            disabled={isFormBusy}
          >
            <Button type="primary" size="small" disabled={isFormBusy}>
              {t("Approve")}
            </Button>
          </Popconfirm>
          <Popconfirm
            title={t("Deny_Request_Confirm")}
            onConfirm={() => handleDeny(record.request)}
            okText={t("Confirm")}
            cancelText={t("Cancel")}
            disabled={isFormBusy}
          >
            <Button danger size="small" disabled={isFormBusy}>
              {t("Deny")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <CustomTable
      tableHeaders={
        <TableHeaderInfo
          title={t("Pending_Join_Requests")}
          count={joinRequests.length}
          countLabel={t("Total_Records_Found")}
        />
      }
      tableProps={{
        dataSource: joinRequests,
        columns,
        loading: isFormBusy,
        pagination: { pageSize: 10 },
        rowKey: (record) => record.request.id,
        bordered: true,
        scroll: { x: 1200 },
      }}
    />
  );
};
